import { Injectable, isDevMode } from '@angular/core';
import { EMPTY, Subject } from 'rxjs';
import { UserService } from 'src/app/services/user-service/user.service';
import { TicketsDaoService } from 'src/app/dao/tickets-dao-service/tickets-dao.service';
import { UserStorageService } from '../user-storage-service/user-storage.service';
import { Ticket } from 'src/app/models/ticket-models/ticket.model';
import { CenterLite } from 'src/app/models/center-model/center-lite.model';
import { tap } from 'rxjs/internal/operators/tap';
import { map } from 'rxjs/internal/operators/map';

@Injectable({
  providedIn: 'root'
})
export class TicketsService {

  accessToken = 'TIC';
  refreshTicketsListener = new Subject();
  refreshTicketListener = new Subject();
  searchTicketsListener = new Subject<any>();
  filterTicketsListener = new Subject<any>();
  toggleTicketsDispositionListener = new Subject<any>();

  constructor(private ticketsDaoService: TicketsDaoService,
    private userStorageService: UserStorageService,
    private userService: UserService) { }

  public getTicketList() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.getTicketList(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const tickets = new Array<Ticket>();
          o['data']['ticketList'].forEach(
            (t) => {
              const ticket = new Ticket();
              Object.assign(ticket, t);

              const centers = new Array<CenterLite>();
              t['centers'].forEach(c => {
                const center = new CenterLite();
                Object.assign(center, c);
                centers.push(center);
              });
              ticket.Centers = centers;

              tickets.push(ticket);
            }
          );
          return tickets;
        })
      );
    }
    return EMPTY;
  }

  public getTicketCounter() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.getTicketCounter(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          return {
            availableTickets: o['data']['counters']['nbAvailables'],
            unavailableTickets: o['data']['counters']['nbDisabled']
          };
        })
      );
    }
    return EMPTY;
  }

  public getTicketListByCenters(centerInternalIds) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.getTicketListByCenters(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalIds).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const tickets = new Array<Ticket>();
          o['data']['ticketList'].forEach(
            (t) => {
              const ticket = new Ticket();
              Object.assign(ticket, t);

              const centers = new Array<CenterLite>();
              t['centers'].forEach(c => {
                const center = new CenterLite();
                Object.assign(center, c);
                centers.push(center);
              });
              ticket.Centers = centers;

              tickets.push(ticket);
            }
          );
          return tickets;
        })
      );
    }
    return EMPTY;
  }

  public getTicketInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.getTicketInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const ticket = new Ticket();
          Object.assign(ticket, o['data']['ticketDetails']);
          const centers = new Array<CenterLite>();
          o['data']['ticketDetails']['centers'].forEach(c => {
            const center = new CenterLite();
            Object.assign(center, c);
            centers.push(center);
          });
          ticket.Centers = centers;
          return ticket;
        })
      );
    }
    return EMPTY;
  }

  public addNewTicket(active, ticketName, centerInternalIds, ticketTextFlag, ticketText, ticketHtmlFlag, ticketHtml) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.addNewTicket(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), active, ticketName, centerInternalIds, ticketTextFlag, ticketText, ticketHtmlFlag, ticketHtml).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateTicketInfo(internalId, active, ticketName) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.updateTicketInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, ticketName).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateTicketCenters(internalId, centerInternalIds) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.updateTicketCenters(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, centerInternalIds).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateTicketTemplates(internalId, active, centerInternalId, ticketHtmlFlag, ticketHtml, ticketTextFlag, ticketText) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.updateTicketTemplates(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, centerInternalId, ticketHtmlFlag, ticketHtml, ticketTextFlag, ticketText).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateTicketStatus(internalId, active) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.updateTicketStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteTicket(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.ticketsDaoService.deleteTicket(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getZplPreview(dpmm, width, height, index, zpl) {
    return this.ticketsDaoService.getZplPreview(dpmm, width, height, index, zpl).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public refreshTicketsListen() {
    return this.refreshTicketsListener.asObservable();
  }

  public refreshTickets() {
    this.refreshTicketsListener.next();
  }

  public refreshTicketListen() {
    return this.refreshTicketListener.asObservable();
  }

  public refreshTicket() {
    this.refreshTicketListener.next();
  }

  public searchTickets(filterKey) {
    this.searchTicketsListener.next(filterKey);
  }

  public searchTicketsListen() {
    return this.searchTicketsListener.asObservable();
  }

  public filterTickets(opts) {
    this.filterTicketsListener.next(opts);
  }

  public filterTicketsListen() {
    return this.filterTicketsListener.asObservable();
  }

  public toggleTicketsDisposition(viewDis) {
    this.toggleTicketsDispositionListener.next(viewDis);
  }

  public toggleTicketsDispositionListen() {
    return this.toggleTicketsDispositionListener.asObservable();
  }
}
